// ============================================
// BulkJobApply Demo OS — API Client
// ============================================
// All Demo OS data goes through these functions.
// They call the backend API (MongoDB) with 
// localStorage as a fast cache.
// ============================================

const API = process.env.REACT_APP_BACKEND_URL || "";

// ── LEADS ───────────────────────────────────────

const LEADS_CACHE_KEY = "bja_leads";

export async function fetchLeads() {
  try {
    const resp = await fetch(`${API}/api/demo/leads`);
    if (resp.ok) {
      const data = await resp.json();
      const leads = data.leads || [];
      localStorage.setItem(LEADS_CACHE_KEY, JSON.stringify(leads));
      return leads;
    }
  } catch (e) {
    console.error("Failed to fetch leads from API:", e);
  }
  // Fallback to cache
  try {
    return JSON.parse(localStorage.getItem(LEADS_CACHE_KEY)) || [];
  } catch {
    return [];
  }
}

export function getLeadsCache() {
  try {
    return JSON.parse(localStorage.getItem(LEADS_CACHE_KEY)) || [];
  } catch {
    return [];
  }
}

export async function saveLead(lead) {
  // Optimistic: update cache immediately
  const cached = getLeadsCache();
  const exists = cached.findIndex((l) => l.id === lead.id);
  if (exists >= 0) {
    cached[exists] = { ...cached[exists], ...lead };
  } else {
    cached.push(lead);
  }
  localStorage.setItem(LEADS_CACHE_KEY, JSON.stringify(cached));

  // Persist to API
  try {
    if (exists >= 0) {
      await fetch(`${API}/api/demo/leads/${lead.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(lead),
      });
    } else {
      await fetch(`${API}/api/demo/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(lead),
      });
    }
  } catch (e) {
    console.error("Failed to save lead to API:", e);
  }
}

export async function saveLeadsAll(leads) {
  localStorage.setItem(LEADS_CACHE_KEY, JSON.stringify(leads));
  try {
    await fetch(`${API}/api/demo/leads/bulk`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ leads }),
    });
  } catch (e) {
    console.error("Failed to bulk save leads:", e);
  }
}

export async function updateLeadField(leadId, updates) {
  const cached = getLeadsCache();
  const updated = cached.map((l) => (l.id === leadId ? { ...l, ...updates } : l));
  localStorage.setItem(LEADS_CACHE_KEY, JSON.stringify(updated));
  try {
    await fetch(`${API}/api/demo/leads/${leadId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(updates),
    });
  } catch (e) {
    console.error("Failed to update lead:", e);
  }
  return updated;
}

export async function seedLeads() {
  try {
    const resp = await fetch(`${API}/api/demo/leads/seed`, { method: "POST" });
    const data = await resp.json();
    if (data.seeded) {
      return await fetchLeads();
    }
    return await fetchLeads();
  } catch (e) {
    console.error("Failed to seed leads:", e);
    return getLeadsCache();
  }
}

// ── SETTINGS ────────────────────────────────────

const SETTINGS_CACHE_KEY = "bja_settings";

const DEFAULT_SETTINGS = {
  fathomApiKey: "p-fSl2PWK4iOjSS-zfiwVg.uz-HhdMkw4w1CwSlCfkjFdRC54M2fKiGVC4zpC6mUss",
  fathomWebhookSecret: "whsec_bbbR1SR0v/d06gxhgPDT2WmYBfmCWWF/",
};

export async function fetchSettings() {
  try {
    const resp = await fetch(`${API}/api/demo/settings`);
    if (resp.ok) {
      const data = await resp.json();
      const settings = { ...DEFAULT_SETTINGS, ...data };
      localStorage.setItem(SETTINGS_CACHE_KEY, JSON.stringify(settings));
      return settings;
    }
  } catch (e) {
    console.error("Failed to fetch settings:", e);
  }
  return getSettingsCache();
}

export function getSettingsCache() {
  try {
    const saved = JSON.parse(localStorage.getItem(SETTINGS_CACHE_KEY)) || {};
    return { ...DEFAULT_SETTINGS, ...saved };
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
}

export async function saveSettingsAPI(settings) {
  localStorage.setItem(SETTINGS_CACHE_KEY, JSON.stringify(settings));
  try {
    await fetch(`${API}/api/demo/settings`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(settings),
    });
  } catch (e) {
    console.error("Failed to save settings:", e);
  }
}

// ── WHATSAPP ─────────────────────────────────────

export async function waGetStatus() {
  const resp = await fetch(`${API}/api/demo/whatsapp/status`);
  return resp.json();
}

export async function waSendMessage(to, message, leadId) {
  const resp = await fetch(`${API}/api/demo/whatsapp/send`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ to, message, leadId }),
  });
  if (!resp.ok) {
    const err = await resp.json();
    throw new Error(err.detail || "Send failed");
  }
  return resp.json();
}

export async function waSendTemplate(to, templateName, language = "en_US", components = [], leadId = null) {
  const resp = await fetch(`${API}/api/demo/whatsapp/send-template`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ to, templateName, language, components, leadId }),
  });
  if (!resp.ok) {
    const err = await resp.json();
    throw new Error(err.detail || "Template send failed");
  }
  return resp.json();
}

export async function waGetMessages(leadId) {
  const resp = await fetch(`${API}/api/demo/whatsapp/messages/${leadId}`);
  return resp.json();
}

export async function waGetAllMessages(limit = 100) {
  const resp = await fetch(`${API}/api/demo/whatsapp/messages?limit=${limit}`);
  return resp.json();
}

export async function waGetChat(phone) {
  const resp = await fetch(`${API}/api/demo/whatsapp/chat/${encodeURIComponent(phone)}`);
  return resp.json();
}

export async function waSendFollowUp(leadId, templateName = "hello_world", language = "en_US", components = []) {
  const resp = await fetch(`${API}/api/demo/whatsapp/follow-up/${leadId}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ templateName, language, components }),
  });
  if (!resp.ok) {
    const err = await resp.json();
    throw new Error(err.detail || "Follow-up failed");
  }
  return resp.json();
}

export async function waGetTemplates() {
  const resp = await fetch(`${API}/api/demo/whatsapp/templates`);
  if (!resp.ok) {
    const err = await resp.json();
    throw new Error(err.detail || "Failed to fetch templates");
  }
  return resp.json();
}

export async function waSaveSettings(settings) {
  const resp = await fetch(`${API}/api/demo/whatsapp/settings`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(settings),
  });
  return resp.json();
}

// ── AUTO-REPLY & RULES ──────────────────────────

export async function waGetAutoReplySettings() {
  const resp = await fetch(`${API}/api/demo/whatsapp/auto-reply-settings`);
  return resp.json();
}

export async function waSaveAutoReplySettings(settings) {
  const resp = await fetch(`${API}/api/demo/whatsapp/auto-reply-settings`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(settings),
  });
  return resp.json();
}

export async function waGetFollowUpRules() {
  const resp = await fetch(`${API}/api/demo/whatsapp/follow-up-rules`);
  return resp.json();
}

export async function waSaveFollowUpRules(rules) {
  const resp = await fetch(`${API}/api/demo/whatsapp/follow-up-rules`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ rules }),
  });
  return resp.json();
}

export async function waStartSequence(leadId) {
  const resp = await fetch(`${API}/api/demo/whatsapp/start-sequence/${leadId}`, { method: "POST" });
  return resp.json();
}

export async function waPauseSequence(leadId) {
  const resp = await fetch(`${API}/api/demo/whatsapp/pause-sequence/${leadId}`, { method: "POST" });
  return resp.json();
}

export async function waResumeSequence(leadId) {
  const resp = await fetch(`${API}/api/demo/whatsapp/resume-sequence/${leadId}`, { method: "POST" });
  return resp.json();
}

export async function waExecuteStep(leadId, step, { message, templateName }) {
  const resp = await fetch(`${API}/api/demo/whatsapp/execute-step/${leadId}/${step}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ message, templateName }),
  });
  if (!resp.ok) {
    const err = await resp.json();
    throw new Error(err.detail || "Step execution failed");
  }
  return resp.json();
}

export async function waGetUnread() {
  const resp = await fetch(`${API}/api/demo/whatsapp/unread`);
  return resp.json();
}

export async function waMarkRead(leadId) {
  const resp = await fetch(`${API}/api/demo/whatsapp/mark-read/${leadId}`, { method: "POST" });
  return resp.json();
}

export async function waGetReminderSettings() {
  const resp = await fetch(`${API}/api/demo/whatsapp/reminder-settings`);
  return resp.json();
}

export async function waSaveReminderSettings(settings) {
  const resp = await fetch(`${API}/api/demo/whatsapp/reminder-settings`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(settings),
  });
  return resp.json();
}

export async function waCheckReminders() {
  const resp = await fetch(`${API}/api/demo/whatsapp/check-reminders`, { method: "POST" });
  return resp.json();
}

// ── AUTOMATION STUDIO (dynamic rules config) ────
export async function getAutomationConfig() {
  const resp = await fetch(`${API}/api/demo/whatsapp/automation/config`);
  if (!resp.ok) throw new Error("Failed to load automation config");
  return resp.json();
}

export async function saveAutomationConfig(config) {
  const resp = await fetch(`${API}/api/demo/whatsapp/automation/config`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ config }),
  });
  if (!resp.ok) {
    const err = await resp.json();
    throw new Error(err.detail || "Save failed");
  }
  return resp.json();
}

export async function resetAutomationConfig() {
  const resp = await fetch(`${API}/api/demo/whatsapp/automation/reset`, { method: "POST" });
  return resp.json();
}

export async function previewAutomationMessage(template, leadId = null) {
  const resp = await fetch(`${API}/api/demo/whatsapp/automation/preview`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ template, leadId }),
  });
  return resp.json();
}

// ── AI TEST LAB (zero-side-effect sandbox) ────
export async function sandboxAiReply(payload) {
  const resp = await fetch(`${API}/api/demo/whatsapp/automation/sandbox/ai-reply`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({}));
    throw new Error(err.detail || `AI call failed (${resp.status})`);
  }
  return resp.json();
}

export async function sandboxReminders(payload) {
  const resp = await fetch(`${API}/api/demo/whatsapp/automation/sandbox/reminders`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  return resp.json();
}

export async function sandboxSequence(payload) {
  const resp = await fetch(`${API}/api/demo/whatsapp/automation/sandbox/sequence`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  return resp.json();
}

export async function flowHelperAsk(message, history = []) {
  const resp = await fetch(`${API}/api/demo/whatsapp/automation/flow-helper`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ message, history }),
  });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({}));
    throw new Error(err.detail || `Flow Helper failed (${resp.status})`);
  }
  return resp.json();
}

export async function waSendPdf(leadId, pdfBase64, filename = "BulkJobApply_Report.pdf") {
  const resp = await fetch(`${API}/api/demo/whatsapp/send-pdf/${leadId}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ pdfBase64, filename }),
  });
  if (!resp.ok) {
    const err = await resp.json();
    throw new Error(err.detail || "PDF send failed");
  }
  return resp.json();
}

export async function waGetAnalytics(days = 30) {
  const resp = await fetch(`${API}/api/demo/whatsapp/analytics?days=${days}`);
  return resp.json();
}

// ── LEAD DELETE ─────────────────────────────────────────

export async function deleteLeadAPI(leadId) {
  const resp = await fetch(`${API}/api/demo/leads/${leadId}`, { method: "DELETE" });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({}));
    throw new Error(err.detail || "Delete failed");
  }
  // Drop from local cache too
  try {
    const cached = JSON.parse(localStorage.getItem(LEADS_CACHE_KEY) || "[]");
    localStorage.setItem(LEADS_CACHE_KEY, JSON.stringify(cached.filter((l) => l.id !== leadId)));
  } catch { /* ignore */ }
  return resp.json();
}

// ── BOOKING FLOW (WhatsApp Calendly-style) ──────────────

export async function waFlowGetStatus() {
  const resp = await fetch(`${API}/api/demo/whatsapp/flow/status`);
  return resp.json();
}

export async function waFlowSetup(force = false) {
  const resp = await fetch(`${API}/api/demo/whatsapp/flow/setup${force ? "?force_recreate=true" : ""}`, { method: "POST" });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({}));
    throw new Error(err.detail || "Flow setup failed");
  }
  return resp.json();
}

export async function waFlowSendToLead(leadId, testMode = false) {
  const resp = await fetch(`${API}/api/demo/whatsapp/flow/send/${leadId}${testMode ? "?test_mode=true" : ""}`, { method: "POST" });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({}));
    throw new Error(err.detail || "Flow send failed");
  }
  return resp.json();
}

// ── AUTH ─────────────────────────────────────────

export async function loginAPI(username, password) {
  const resp = await fetch(`${API}/api/demo/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username, password }),
  });
  if (!resp.ok) {
    throw new Error("Invalid credentials");
  }
  const data = await resp.json();
  localStorage.setItem("bja_auth", JSON.stringify(data));
  return data;
}

// ── EXPORTS (Admin only) ─────────────────────────
export function _getAuthToken() {
  try {
    const raw = localStorage.getItem("bja_auth");
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.token || null;
  } catch {
    return null;
  }
}

async function _downloadCsv(path, filename) {
  const token = _getAuthToken();
  if (!token) throw new Error("Please log out and log back in as admin to enable exports.");
  const resp = await fetch(`${API}${path}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!resp.ok) {
    if (resp.status === 403) throw new Error("Admin access required. Log in with the admin account.");
    if (resp.status === 401) throw new Error("Session expired. Please log in again.");
    throw new Error(`Export failed (${resp.status})`);
  }
  const blob = await resp.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export async function downloadLandingLeadsCsv() {
  const ts = new Date().toISOString().slice(0, 10);
  return _downloadCsv(`/api/landing/leads?format=csv&limit=10000`, `landing_leads_${ts}.csv`);
}

export async function downloadAuditLeadsCsv() {
  const ts = new Date().toISOString().slice(0, 10);
  return _downloadCsv(`/api/audit/leads?format=csv&limit=10000`, `audit_leads_${ts}.csv`);
}

export async function fetchLandingLeadsSummary() {
  const token = _getAuthToken();
  if (!token) throw new Error("Sign in as admin first.");
  const resp = await fetch(`${API}/api/landing/leads?format=json&limit=5`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!resp.ok) throw new Error(`Fetch failed (${resp.status})`);
  return resp.json();
}

// ── CAPI Backfill ────────────────────────────────

export async function capiBackfillPurchase({ dry_run = false, force = false } = {}) {
  const resp = await fetch(`${API}/api/demo/leads/backfill-capi-purchase`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ dry_run, force }),
  });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({}));
    throw new Error(err.detail || `Backfill failed (${resp.status})`);
  }
  return resp.json();
}

export async function capiEventsList({ limit = 25, event_name = null } = {}) {
  const qs = new URLSearchParams({ limit: String(limit) });
  if (event_name) qs.set("event_name", event_name);
  const resp = await fetch(`${API}/api/capi-events?${qs.toString()}`);
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({}));
    throw new Error(err.detail || `Fetch failed (${resp.status})`);
  }
  return resp.json();
}

